<template>
  <div class="sidebar" :class="sidebarClasses">
    <div class="sidebar-content">
      <!-- Загрузка -->
      <div v-if="isLoading" class="sidebar-loading">
        <ProgressSpinner style="width: 30px; height: 30px" />
        <span>Загрузка меню...</span>
      </div>

      <div v-else-if="!hasMenu && !isLoading" class="sidebar-error">
        <i class="pi pi-exclamation-triangle error-icon"></i>
        <span>Меню недоступно</span>
        <Button label="Повторить" class="p-button-text p-button-sm" @click="handleRetry" />
      </div>

      <!-- Дерево меню -->
      <div v-else class="sidebar-menu-body">
        <div v-if="!isMini" class="sidebar-nav-toolbar">
          <Button type="button" icon="pi pi-minus" class="p-button-text p-button-sm sidebar-bulk-btn"
            title="Свернуть всё" aria-label="Свернуть всё" @click="collapseMenuTree" />
          <Button type="button" icon="pi pi-plus" class="p-button-text p-button-sm sidebar-bulk-btn"
            title="Развернуть всё" aria-label="Развернуть всё" @click="expandMenuTree" />
        </div>
        <nav class="sidebar-nav">
          <NavMenuItem v-for="item in sideMenu" :key="item.branch_id" :item="item" :level="0" :is-mini="isMini"
            @item-click="handleItemClick" />
        </nav>
      </div>
    </div>

    <!-- Кнопка сворачивания -->
    <button v-if="!isMobile" class="sidebar-toggle" @click="$emit('toggle-mini')">
      <i :class="isMini ? 'pi pi-chevron-right' : 'pi pi-chevron-left'"></i>
    </button>
  </div>

  <!-- Overlay для мобильных -->
  <div v-if="isMobile && isOpen" class="sidebar-overlay" @click="$emit('close')" />
</template>

<script setup lang="ts">
import En from '@/common/classes/En'
import { useMenu } from '@/common/composables/useMenu'
import { EnKey } from '@/components/platform/injection-keys'
import { navigateMenuItem } from '@/services/menu/menuItemNavigation'
import { useAppStore } from '@/stores/app'
import { useDocumentTabsStore } from '@/stores/documentTabs'
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import { computed, inject, provide, ref, nextTick, type ComputedRef } from 'vue'
import { useRouter } from 'vue-router'
import NavMenuItem from './NavMenuItem.vue'
import { MenuTreeBulkKey } from './menuTreeBulk'

interface Props {
  isOpen?: boolean
  isMini?: boolean
  isMobile?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: true,
  isMini: false,
  isMobile: false
})

const emit = defineEmits<{
  'toggle-mini': []
  'close': []
}>()

const router = useRouter()
const appStore = useAppStore()
const documentTabsStore = useDocumentTabsStore()
const en = inject<ComputedRef<En | null>>(EnKey, null)

// Используем composable для меню
const { sideMenu, isLoading, hasMenu, hasChildren } = useMenu()

const MENU_EXPAND_WAVES = 28
const collapseSignal = ref(0)
const expandSignal = ref(0)
const suppressRouteExpansion = ref(false)

provide(MenuTreeBulkKey, {
  collapseSignal,
  expandSignal,
  suppressRouteExpansion,
})

function collapseMenuTree() {
  suppressRouteExpansion.value = true
  collapseSignal.value++
}

async function expandMenuTree() {
  suppressRouteExpansion.value = false
  for (let i = 0; i < MENU_EXPAND_WAVES; i++) {
    expandSignal.value++
    await nextTick()
  }
}

interface MenuClickPayload {
  item: any
  event: MouseEvent
}

// Отслеживаем открытые элементы меню
const openItems = ref<Set<number>>(new Set())

const sidebarClasses = computed(() => [
  'sidebar',
  {
    'sidebar--open': props.isOpen,
    'sidebar--mini': props.isMini,
    'sidebar--mobile': props.isMobile
  }
])

function openBrowserTab(href: string) {
  const form = document.createElement('form')
  form.method = 'get'
  form.action = href
  form.target = '_blank'
  form.style.display = 'none'
  document.body.appendChild(form)
  form.submit()
  form.remove()
}

// Переключение открытого состояния
const toggleItem = (item: any) => {
  if (openItems.value.has(item.branch_id)) {
    openItems.value.delete(item.branch_id)
  } else {
    openItems.value.add(item.branch_id)
  }
}

// Обработка клика по пункту меню
const handleItemClick = async ({ item, event }: MenuClickPayload) => {

  if (hasChildren(item)) {
    // Для элементов с детьми - toggle открытия
    toggleItem(item)
    if (props.isMini && !props.isMobile) {
      emit('toggle-mini')
    }
  } else {
    suppressRouteExpansion.value = false
    const navDeps = {
      router,
      appStore,
      documentTabsStore,
      en: en?.value ?? null,
    }
    if (event.shiftKey && item.branch_id) {
      const target = router.resolve({
        name: 'platform',
        params: { id: item.branch_id.toString() },
      })
      openBrowserTab(target.href)
    } else if ((event.altKey || item.openInNewTab === true) && item.branch_id) {
      await navigateMenuItem(item, navDeps, { openInBackground: true })
    } else {
      await navigateMenuItem(item, navDeps)
    }

    if (props.isMobile) {
      emit('close')
    }
  }
}

const getItemPath = (item: any): any => {
  if (item.branch_id) {
    return {
      name: 'platform',
      params: { id: item.branch_id.toString() }
    }
  }
  return null
}

const handleRetry = () => {
  window.location.reload()
}
</script>


<style scoped lang="scss">
.sidebar {
  position: fixed;
  top: var(--header-height, 4rem);
  bottom: 0;
  left: calc(-1 * var(--sidebar-width, 22rem));
  width: var(--sidebar-width, 22rem);
  background: var(--sidebar-accent, #1d4fd8);
  color: #ffffff;
  z-index: 900;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  &--open {
    left: 0;
  }

  &--mini {
    width: var(--sidebar-mini-width, 5rem);

    .sidebar-content {
      padding-right: 0;
      padding-left: 0;
    }

    .sidebar-nav {
      align-items: center;
      gap: 8px;
    }
  }

  &--mobile {
    z-index: 1100;
    box-shadow: 4px 0 30px rgba(0, 0, 0, 0.3);
    /* Overlay drawer: never leave a visible rail when closed */
    transform: translateX(0);
    visibility: visible;
    pointer-events: auto;

    &:not(.sidebar--open) {
      left: 0;
      transform: translateX(-100%);
      visibility: hidden;
      pointer-events: none;
      box-shadow: none;
    }
  }
}

.sidebar-content {
  flex: 1;
  padding: 0.65rem 0 0.85rem;
  overflow-y: auto;
}

.sidebar-menu-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.sidebar-nav-toolbar {
  display: flex;
  justify-content: flex-end;
  gap: 0.25rem;
  padding: 0 0.5rem 0.5rem;
  margin-bottom: 0.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.sidebar-bulk-btn {
  color: rgba(255, 255, 255, 0.9);

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: white;
  }
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-item {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: all 0.2s ease;

  &-content {
    display: flex;
    align-items: center;
    padding: 0.75rem 1.5rem;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }

  &--active {
    .nav-item-content {
      background: rgba(255, 255, 255, 0.15);
      border-right: 3px solid white;
    }
  }

  &--nested {
    .nav-item-content {
      font-size: 0.9rem;
      border-left: 2px solid rgba(255, 255, 255, 0.2);

      .nav-icon {
        font-size: 1rem;
      }
    }
  }

  &--has-children {
    .nav-item-content {
      font-weight: 600;
    }
  }
}

.nav-children {
  background: rgba(0, 0, 0, 0.1);
  margin-left: 0.5rem;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-icon {
  font-size: 1.25rem;
  margin-right: 1rem;
  transition: margin-right 0.3s ease;
  flex-shrink: 0;
}

.nav-label {
  font-weight: 500;
  transition: all 0.3s ease;
  flex: 1;
}

.nav-arrow {
  font-size: 0.8rem;
  transition: transform 0.3s ease;
  margin-left: auto;

  &--open {
    transform: rotate(180deg);
  }
}

.sidebar-loading,
.sidebar-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  text-align: center;
  gap: 1rem;
}

.error-icon {
  font-size: 2rem;
  color: #ff6b6b;
}

.sidebar-toggle {
  position: absolute;
  top: 50%;
  right: -12px;
  width: 24px;
  height: 48px;
  background: var(--sidebar-accent, #1d4fd8);
  border: none;
  border-radius: 0 12px 12px 0;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;

  &:hover {
    right: -14px;
    box-shadow: 3px 0 12px rgba(0, 0, 0, 0.3);
  }
}

.sidebar-overlay {
  position: fixed;
  top: var(--header-height, 4rem);
  bottom: 0;
  left: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  backdrop-filter: blur(2px);
}

@media (max-width: 960px) {
  .sidebar {
    top: 3.5rem;
  }

  .sidebar-overlay {
    top: 3.5rem;
  }

  .sidebar--mini {
    width: var(--sidebar-width, 22rem);

    .nav-label,
    .nav-arrow {
      opacity: 1;
      visibility: visible;
    }

    .nav-children {
      display: block;
    }
  }
}

@media (max-width: 600px) {
  .sidebar {
    width: 100%;
    max-width: 320px;
  }
}
</style>
